import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import LabFilters, { emptyLabFilter, labFilterIsOn,
                     matchesLabFilter } from '../../components/common/LabFilters';
import { apiRequest, eventDayLabel, eventStartLabel } from '../../utils';
import './MemberHome.css';

/* An event the company bought from us (``manage`` on the row) opens the game Home on that event —
   where its teams are — and not the public event page, which a private project does not have. */
const openManaged = (eventId) => {
  apiRequest('POST', '/manage-event', { event_id: eventId })
    .then((res) => { window.location.href = (res && res.redirect) || '/event-home'; })
    .catch(() => {});
};


const fallbackEvent = require('./../../assets/images/fallback/event.png');

/* Every upcoming Bravos Lab — reached from the "Show more" under the member/contact home
   calendar, which shows only the next few. Rows open the public event page, where the actual
   booking lives; the per-row actions (book, claim a seat, invite) stay on the home, so this page
   is a plain list and cannot get out of step with them.
   Reuses the same GET /api/bravos/home payload (its calendar carries is_past), so no new
   endpoint — the twin of PastLabs.

   ★ This is the page the whole calendar lands on, so this is where it is sifted: am I in it,
   when is it, who is running it — and, when the events themselves say so, what kind of thing it
   is, which league it is a day of, and what it is for. The controls and the match rule are
   `components/common/LabFilters`, shared with PastLabs. */

const UpcomingLabs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState(null);
  // The two catalogues the chips are drawn out of — the BU's categories and, for a company that
  // runs its own events, what it says it stands for. Held beside the rows because they come off
  // the same payload.
  const [catalog, setCatalog] = useState({ tags: [], purpose: null });
  const [filter, setFilter] = useState(emptyLabFilter());

  useEffect(() => {
    let cancelled = false;
    apiRequest('GET', '/bravos/home')
      .then((data) => {
        if (cancelled) return;
        const rows = (data.calendar || []).filter((e) => !e.is_past);
        rows.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
        setUpcoming(rows);
        setCatalog({ tags: data.tag_catalog || [], purpose: data.purpose_catalog });
      })
      .catch(() => { if (!cancelled) setUpcoming([]); });
    return () => { cancelled = true; };
  }, []);

  const shown = (upcoming || []).filter((e) => matchesLabFilter(e, filter));

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="calendar" theme="primary" className="me-2" />
          {t('upcomingEvents')}
        </Title>

        {/* Only worth drawing once there is more than one event to sift. */}
        {upcoming !== null && upcoming.length > 1 && (
          <LabFilters
            events={upcoming}
            tagCatalog={catalog.tags}
            purposeCatalog={catalog.purpose}
            value={filter}
            onChange={setFilter}
          />
        )}

        {upcoming === null ? (
          <span className="auth__spinner" />
        ) : upcoming.length === 0 ? (
          <p className="text-muted mb-0">{t('noUpcomingEvents')}</p>
        ) : shown.length === 0 && labFilterIsOn(filter) ? (
          <p className="text-muted mb-0">{t('noEventsForFilter')}</p>
        ) : (
          shown.map((e) => (
            <div
              key={e.id}
              className="bravos-event"
              role="button"
              tabIndex={0}
              onClick={() => (e.manage ? openManaged(e.id) : navigate(`/public-event/${e.id}`))}
              onKeyDown={(ev) => { if (ev.key === 'Enter') { if (e.manage) openManaged(e.id); else navigate(`/public-event/${e.id}`); } }}
            >
              <span
                className="bravos-event__thumb"
                style={{ backgroundImage: `url("${e.image_link || fallbackEvent}")` }}
              />
              <span className="bravos-event__body">
                <span className="bravos-event__name">{e.name}</span>
                {/* Same day · start · place as the home calendar this page is the rest of —
                    and the start time in the EVENT's zone, so the two never disagree. */}
                <span className="bravos-event__meta">
                  {[eventDayLabel(e), eventStartLabel(e), e.location_name]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              {e.attending && (
                <span className="bravos-event__pill bravos-event__pill--in">
                  {t('attendingBadge')}
                </span>
              )}
              <Icon name="angle-right" className="bravos-event__chev" />
            </div>
          ))
        )}
      </Card>
    </Base>
  );
};

export default UpcomingLabs;
